"use client"

import * as React from "react"

import { XIcon } from "@/components/ui/icons"
import {
  ClientPortal,
  Slot,
  mergeRefs,
  useControllableState,
  useDismissableLayer,
  useEscapeKey,
  useScrollLock,
} from "@/components/ui/internal"
import { dialogClassNames } from "@/components/ui/uiClasses"
import { cn } from "@/lib/utils"

type DialogContextValue = {
  open: boolean
  setOpen: (open: boolean) => void
  triggerRef: React.RefObject<HTMLElement | null>
  titleId: string
  descriptionId: string
}

const DialogContext = React.createContext<DialogContextValue | null>(null)

function useDialogContext(component: string) {
  const context = React.useContext(DialogContext)
  if (!context) {
    throw new Error(`${component} must be used within Dialog`)
  }
  return context
}

function Dialog({
  open,
  defaultOpen = false,
  onOpenChange,
  children,
}: {
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  children: React.ReactNode
}) {
  const [isOpen, setIsOpen] = useControllableState({
    prop: open,
    defaultProp: defaultOpen,
    onChange: onOpenChange,
  })
  const triggerRef = React.useRef<HTMLElement | null>(null)
  const titleId = React.useId()
  const descriptionId = React.useId()

  return (
    <DialogContext.Provider
      value={{
        open: Boolean(isOpen),
        setOpen: (nextOpen) => setIsOpen(nextOpen),
        triggerRef,
        titleId,
        descriptionId,
      }}
    >
      {children}
    </DialogContext.Provider>
  )
}

const DialogTrigger = React.forwardRef<
  HTMLElement,
  React.HTMLAttributes<HTMLElement> & {
    asChild?: boolean
    children?: React.ReactNode
    type?: "button" | "submit" | "reset"
  }
>(function DialogTrigger(
  { asChild = false, onClick, children, ...props },
  forwardedRef
) {
  const dialog = useDialogContext("DialogTrigger")

  if (asChild) {
    return (
      <Slot
        ref={mergeRefs(forwardedRef, dialog.triggerRef)}
        data-slot="dialog-trigger"
        aria-haspopup="dialog"
        aria-expanded={dialog.open}
        onClick={(event) => {
          onClick?.(event)
          dialog.setOpen(true)
        }}
        {...props}
      >
        {children as React.ReactElement}
      </Slot>
    )
  }

  return (
    <button
      ref={mergeRefs(
        forwardedRef as React.Ref<HTMLButtonElement>,
        dialog.triggerRef as React.Ref<HTMLButtonElement>
      )}
      data-slot="dialog-trigger"
      aria-haspopup="dialog"
      aria-expanded={dialog.open}
      type={props.type ?? "button"}
      onClick={(event) => {
        onClick?.(event)
        dialog.setOpen(true)
      }}
      {...props}
    >
      {children}
    </button>
  )
})

function DialogPortal({ children }: { children: React.ReactNode }) {
  const dialog = useDialogContext("DialogPortal")

  if (!dialog.open) {
    return null
  }

  return <ClientPortal>{children}</ClientPortal>
}

const DialogClose = React.forwardRef<
  HTMLElement,
  React.HTMLAttributes<HTMLElement> & {
    asChild?: boolean
    children?: React.ReactNode
    type?: "button" | "submit" | "reset"
  }
>(function DialogClose(
  { asChild = false, onClick, children, ...props },
  forwardedRef
) {
  const dialog = useDialogContext("DialogClose")

  if (asChild) {
    return (
      <Slot
        ref={forwardedRef}
        data-slot="dialog-close"
        onClick={(event) => {
          onClick?.(event)
          dialog.setOpen(false)
        }}
        {...props}
      >
        {children as React.ReactElement}
      </Slot>
    )
  }

  return (
    <button
      ref={forwardedRef as React.Ref<HTMLButtonElement>}
      data-slot="dialog-close"
      type={props.type ?? "button"}
      onClick={(event) => {
        onClick?.(event)
        dialog.setOpen(false)
      }}
      {...props}
    >
      {children}
    </button>
  )
})

const DialogOverlay = React.forwardRef<HTMLDivElement, React.ComponentProps<"div">>(
  function DialogOverlay({ className, ...props }, ref) {
    return (
      <div
        ref={ref}
        data-slot="dialog-overlay"
        data-state="open"
        className={cn(dialogClassNames.overlay, className)}
        {...props}
      />
    )
  }
)

const DialogContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    showCloseButton?: boolean
    closeButtonClassName?: string
    overlayClassName?: string
    showOverlay?: boolean
    unstyled?: boolean
    scrollLockTarget?: "body" | "main" | "none"
  }
>(function DialogContent(
  {
    className,
    children,
    showCloseButton = true,
    closeButtonClassName,
    overlayClassName,
    showOverlay = true,
    unstyled = false,
    scrollLockTarget = "body",
    "aria-describedby": ariaDescribedBy,
    onClick,
    ...props
  },
  forwardedRef
) {
  const dialog = useDialogContext("DialogContent")
  const contentRef = React.useRef<HTMLDivElement | null>(null)
  const scrollLockElement =
    typeof document === "undefined"
      ? null
      : scrollLockTarget === "none"
        ? null
        : scrollLockTarget === "main"
          ? ((dialog.triggerRef.current?.closest("main") as HTMLElement | null) ??
            (document.querySelector("main") as HTMLElement | null))
          : document.body

  useScrollLock(dialog.open, scrollLockElement)
  useEscapeKey(dialog.open, () => dialog.setOpen(false))
  useDismissableLayer({
    enabled: dialog.open && !showOverlay,
    refs: [dialog.triggerRef, contentRef],
    onDismiss: () => dialog.setOpen(false),
  })

  React.useEffect(() => {
    if (!dialog.open) return
    contentRef.current?.focus()
  }, [dialog.open])

  if (!dialog.open) {
    return null
  }

  return (
    <DialogPortal>
      {showOverlay ? (
        <DialogOverlay
          className={overlayClassName}
          onClick={() => dialog.setOpen(false)}
        />
      ) : null}
      <div className={dialogClassNames.viewport}>
        <div
          ref={mergeRefs(forwardedRef, contentRef)}
          data-slot="dialog-content"
          data-state="open"
          role="dialog"
          aria-modal="true"
          aria-labelledby={dialog.titleId}
          aria-describedby={ariaDescribedBy ?? dialog.descriptionId}
          tabIndex={-1}
          className={cn(
            unstyled
              ? dialogClassNames.contentUnstyled
              : dialogClassNames.content,
            className
          )}
          onClick={(event) => {
            event.stopPropagation()
            onClick?.(event)
          }}
          {...props}
        >
          {children}
          {showCloseButton ? (
            <DialogClose
              className={cn(
                dialogClassNames.closeButton,
                closeButtonClassName
              )}
            >
              <XIcon />
              <span className="sr-only">Close</span>
            </DialogClose>
          ) : null}
        </div>
      </div>
    </DialogPortal>
  )
})

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(dialogClassNames.header, className)}
      {...props}
    />
  )
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(dialogClassNames.footer, className)}
      {...props}
    />
  )
}

const DialogTitle = React.forwardRef<
  HTMLHeadingElement,
  React.ComponentProps<"h2">
>(function DialogTitle({ className, ...props }, ref) {
  const dialog = useDialogContext("DialogTitle")

  return (
    <h2
      ref={ref}
      id={props.id ?? dialog.titleId}
      data-slot="dialog-title"
      className={cn(dialogClassNames.title, className)}
      {...props}
    />
  )
})

const DialogDescription = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentProps<"p">
>(function DialogDescription({ className, ...props }, ref) {
  const dialog = useDialogContext("DialogDescription")

  return (
    <p
      ref={ref}
      id={props.id ?? dialog.descriptionId}
      data-slot="dialog-description"
      className={cn(dialogClassNames.description, className)}
      {...props}
    />
  )
})

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
